import { AxiosInstance } from 'axios'
import {
  GetTransactionsParams,
  TransactionDetailed,
  TransactionShort,
  UpdateTransactionPayload,
  UpdateTransactionsPayload
} from 'types/transactions'
import { TRANSACTIONS_ENDPOINTS } from 'api/ApiRoutes'
import { cleanObject, formatDateParams } from 'utils/Functions'
import qs from 'qs'
import { DateRangeFilterInAPI } from 'types/reports'
import { Team } from 'types/groups'

class TransactionsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getTransaction = async ({
    transactionId
  }: {
    transactionId: TransactionShort['token_id']
  }) => {
    const { data } = await this.axiosInstance.get<TransactionDetailed>(
      TRANSACTIONS_ENDPOINTS.TRANSACTION(transactionId)
    )
    return data
  }

  getTransactions = async (params: Partial<GetTransactionsParams>) => {
    const { date_range, purpose, ...rest } = params ?? {}
    const apiParams = cleanObject({
      ...rest,
      purpose: purpose !== 'Unclassified' ? purpose : undefined,
      ...formatDateParams(date_range ?? [])
    })
    const { data } = await this.axiosInstance.get<TransactionDetailed[]>(
      TRANSACTIONS_ENDPOINTS.BASE,
      {
        params: apiParams
      }
    )
    return data
  }

  createTransaction = async (
    transaction: UpdateTransactionPayload | FormData,
    headers?: { 'Content-Type': string }
  ) => {
    const { data } = await this.axiosInstance.post<TransactionShort>(
      TRANSACTIONS_ENDPOINTS.BASE,
      transaction instanceof FormData ? transaction : { transaction },
      { headers }
    )
    return data
  }

  updateTransaction = async (
    transactionId: TransactionShort['token_id'],
    transaction: UpdateTransactionPayload | FormData,
    headers?: { 'Content-Type': string }
  ) => {
    const { data } = await this.axiosInstance.put<TransactionShort>(
      TRANSACTIONS_ENDPOINTS.TRANSACTION(transactionId),
      transaction instanceof FormData ? transaction : { transaction },
      { headers }
    )
    return data
  }

  batchDeleteTransactions = async (
    payload: UpdateTransactionsPayload['filter_params']
  ) => {
    const { date_range, ...rest } = payload ?? {}
    const apiPayload = cleanObject({
      ...rest,
      ...formatDateParams(date_range !== null ? date_range : []),
      limit: null
    })

    const query = qs.stringify(
      { filter_params: apiPayload },
      { arrayFormat: 'brackets' }
    )
    const { data } = await this.axiosInstance.delete(
      `${TRANSACTIONS_ENDPOINTS.BATCH_DELETE_TRANSACTIONS}?${query}`
    )
    return data
  }

  batchUpdateTransactions = ({
    filter_params,
    update_params
  }: UpdateTransactionsPayload) => {
    const { date_range, ...rest } = filter_params ?? {}
    const modified_filter_params = cleanObject({
      ...rest,
      ...formatDateParams<DateRangeFilterInAPI>(date_range),
      uncategorized: rest.purpose === 'uncategorized' ? true : null,
      limit: null
    })
    const payload = {
      filter_params: modified_filter_params,
      update_params
    }
    return this.axiosInstance.put(
      TRANSACTIONS_ENDPOINTS.BATCH_UPDATE_TRANSACTIONS,
      payload
    )
  }

  getReportTransaction = async ({
    transactionId,
    teamId
  }: {
    transactionId: TransactionShort['token_id']
    teamId: Team['token_id']
  }) => {
    const { data } = await this.axiosInstance.get<TransactionDetailed>(
      TRANSACTIONS_ENDPOINTS.TEAM_TRANSACTION(teamId, transactionId)
    )
    return data
  }
}

export default TransactionsApi
